/* eslint-disable @typescript-eslint/no-explicit-any */
import { _getCacheKey, DEFAULT_TTL } from './cache'
import { _buildQueryString, _buildUrlWithUrlParams } from './tools'
import { _FetchConfigData, _FetchOptions, _RequestDefinition } from './types'

type NormalizedRequestConfig = {
  method: string
  headers: Record<string, string>
  url: string
  baseUrl?: string
  body?: any
  signal?: AbortSignal
}

export const _doFetch =
  (fetchConfigData: _FetchConfigData) =>
  <Def extends _RequestDefinition<any, any, any>>(
    def: Def,
    options?: _FetchOptions<Def['urlParams'], Def['fetchParams']> & {
      signal?: AbortSignal
    }
  ): Promise<Def['response']> => {
    const { cache, inflight, requestMiddlewares, errorMiddlewares } = fetchConfigData

    const merged = { ...def, ...options }
    const {
      urlParams,
      fetchParams,
      useCache = def.method === 'GET',
      retry = 0,
      retryDelay = 0,
      ttl = DEFAULT_TTL,
      signal
    } = merged

    const rawUrl = _buildUrlWithUrlParams(def.url, urlParams as any)

    const initialConfig: NormalizedRequestConfig = {
      method: def.method,
      headers: { 'Content-Type': 'application/json' },
      url: rawUrl,
      signal
    }

    if (def.method !== 'GET' && fetchParams) {
      initialConfig.body = JSON.stringify(fetchParams)
    } else if (def.method === 'GET' && fetchParams) {
      initialConfig.url += _buildQueryString(fetchParams as any)
    }

    // apply request middlewares
    let configPromise = Promise.resolve(initialConfig)
    for (const mw of requestMiddlewares) {
      configPromise = configPromise.then((conf) =>
        Promise.resolve(mw(conf, { urlParams, fetchParams })) as Promise<NormalizedRequestConfig>
      )
    }

    return configPromise.then((config) => {
      const cacheKey = _getCacheKey({
        url: def.url,
        method: def.method,
        fetchParams,
        urlParams
      })

      if (useCache) {
        if (cache.has(cacheKey)) {
          const cached = cache.get(cacheKey)
          if (cached.expireAt > Date.now()) {
            return Promise.resolve(cached.data)
          }
          cache.delete(cacheKey)
        }
        if (inflight.has(cacheKey)) return inflight.get(cacheKey)
      }

      const doRequest = (retryLeft: number): Promise<any> => {
        return new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest()

          let aborted = false
          const handleAbort = () => {
            aborted = true
            xhr.abort()
            signal?.removeEventListener('abort', handleAbort)
          }

          if (config.signal && typeof config.signal.addEventListener === 'function') {
            config.signal.addEventListener('abort', handleAbort)
          }

          xhr.open(config.method || 'GET', (config.baseUrl || '') + config.url, true)

          for (const key in config.headers) {
            if (Object.prototype.hasOwnProperty.call(config.headers, key)) {
              xhr.setRequestHeader(key, config.headers[key])
            }
          }

          xhr.onreadystatechange = () => {
            if (xhr.readyState !== 4) return

            if (xhr.status >= 200 && xhr.status < 300) {
              const contentType = xhr.getResponseHeader('Content-Type') || ''
              const text = xhr.responseText
              let data: any = text

              if (contentType.includes('application/json')) {
                try {
                  data = JSON.parse(text)
                } catch (_) {
                  // fallback to raw text
                }
              }

              if (useCache) {
                cache.set(cacheKey, {
                  data,
                  expireAt: Date.now() + ttl
                })
              }

              resolve(data)
            } else {
              let errorData: any = xhr.responseText
              try {
                errorData = JSON.parse(xhr.responseText)
              } catch (_) {}

              const error: any = new Error(xhr.statusText || 'Request failed')
              error.response = {
                data: errorData,
                status: xhr.status,
                statusText: xhr.statusText
              }

              reject(error)
            }
          }

          xhr.onerror = () => {
            reject(new Error('Network Error'))
          }

          if (config.method === 'GET' || !config.body) {
            xhr.send()
          } else {
            xhr.send(config.body)
          }
        }).catch((err) => {
          if (retryLeft > 0) {
            return new Promise((res) => setTimeout(res, retryDelay)).then(() =>
              doRequest(retryLeft - 1)
            )
          }

          let errorPromise = Promise.resolve(err)
          for (const emw of errorMiddlewares) {
            errorPromise = errorPromise.then((e) => emw(e, config))
          }

          return errorPromise.then((e) => {
            throw e
          })
        })
      }

      const requestPromise = doRequest(retry)
      if (useCache) inflight.set(cacheKey, requestPromise)

      return requestPromise.then((res) => {
        inflight.delete(cacheKey)
        return res
      })
    })
  }